import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import ScheduleSendRoundedIcon from '@mui/icons-material/ScheduleSendRounded'
import {
  adminService,
  type Campaign,
  type SurveySchedule,
  type TargetingRule,
} from '../../services/adminService'
import type { EventForm, EventInfo } from '../../data/types'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import RichTextEditor from '../components/RichTextEditor'

const TRIGGER_LABELS: Record<string, string> = {
  event_end: 'After the event ends',
  session_end: 'After a session ends',
  fixed: 'At a fixed time',
}

export default function OutreachManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [surveys, setSurveys] = useState<SurveySchedule[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [forms, setForms] = useState<EventForm[]>([])
  const [rules, setRules] = useState<TargetingRule[]>([])
  const [event, setEvent] = useState<EventInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [surveyDialog, setSurveyDialog] = useState(false)
  const [campaignEdit, setCampaignEdit] = useState<Campaign | null | 'new'>(null)
  const [scheduleFor, setScheduleFor] = useState<Campaign | null>(null)

  const load = () =>
    Promise.all([
      adminService.listSurveySchedules(eventId),
      adminService.listCampaigns(eventId),
      adminService.listForms(eventId),
      adminService.listRules(eventId),
      adminService.getEvent(eventId),
    ])
      .then(([s, c, f, r, e]) => {
        setSurveys(s)
        setCampaigns(c)
        setForms(f)
        setRules(r)
        setEvent(e)
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const removeSurvey = async (s: SurveySchedule) => {
    if (!(await confirmDelete('Cancel this scheduled survey?'))) return
    try {
      await adminService.deleteSurveySchedule(eventId, s.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const formName = (id: string) => forms.find((f) => f.id === id)?.title ?? 'Survey'

  if (loading) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Scheduled surveys
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddRoundedIcon />}
          onClick={() => setSurveyDialog(true)}
          disabled={forms.length === 0}
        >
          Schedule survey
        </Button>
      </Stack>
      {forms.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a form in the Forms tab first, then schedule it here.
        </Typography>
      )}
      {surveys.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No scheduled surveys.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {surveys.map((s) => (
            <Card key={s.id} variant="outlined" sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {formName(s.formId)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {TRIGGER_LABELS[s.trigger] ?? s.trigger}
                  {s.offsetMinutes ? ` +${s.offsetMinutes}m` : ''}
                  {s.dueAt ? ` · sends ${new Date(s.dueAt).toLocaleString()}` : ''}
                </Typography>
              </Box>
              <Chip size="small" label={s.status} sx={{ textTransform: 'capitalize' }} />
              <IconButton size="small" color="error" onClick={() => removeSurvey(s)}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>
          Email campaigns
        </Typography>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCampaignEdit('new')}>
          New campaign
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        New campaigns start as a <strong>Draft</strong> so you can review them.
        A draft is never sent on its own — use <strong>Send / schedule</strong>{' '}
        on the campaign to send it now or set a send time.
      </Typography>
      {campaigns.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No campaigns yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {campaigns.map((c) => (
            <CampaignCard
              key={c.id}
              eventId={eventId}
              campaign={c}
              rules={rules}
              event={event}
              onEdit={() => setCampaignEdit(c)}
              onChanged={load}
            />
          ))}
        </Stack>
      )}

      {surveyDialog && (
        <SurveyDialog
          eventId={eventId}
          forms={forms}
          onClose={() => setSurveyDialog(false)}
          onSaved={() => {
            setSurveyDialog(false)
            load()
          }}
        />
      )}
      {campaignEdit && (
        <CampaignDialog
          eventId={eventId}
          campaign={campaignEdit === 'new' ? null : campaignEdit}
          rules={rules}
          onClose={() => setCampaignEdit(null)}
          onSaved={() => {
            setCampaignEdit(null)
            load()
          }}
          onSavedAndSchedule={(saved) => {
            setCampaignEdit(null)
            setScheduleFor(saved)
          }}
        />
      )}
      {scheduleFor && (
        <ScheduleDialog
          eventId={eventId}
          campaignId={scheduleFor.id}
          event={event}
          onClose={() => {
            setScheduleFor(null)
            load()
          }}
          onDone={() => {
            setScheduleFor(null)
            load()
          }}
        />
      )}
    </Box>
  )
}

function CampaignCard({
  eventId,
  campaign,
  rules,
  event,
  onEdit,
  onChanged,
}: {
  eventId: string
  campaign: Campaign
  rules: TargetingRule[]
  event: EventInfo | null
  onEdit: () => void
  onChanged: () => void
}) {
  const { notify } = useToast()
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const seg = campaign.targetingRuleId
    ? rules.find((r) => r.id === campaign.targetingRuleId)?.name ?? 'Segment'
    : 'Everyone'

  const cancel = async () => {
    try {
      await adminService.cancelCampaign(eventId, campaign.id)
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const remove = async () => {
    if (!(await confirmDelete(`Delete campaign "${campaign.name}"?`))) return
    try {
      await adminService.deleteCampaign(eventId, campaign.id)
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {campaign.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {campaign.subject} · to {seg}
            {campaign.status === 'sent' && ` · ${campaign.sentCount ?? 0} sent, ${campaign.openCount ?? 0} opened`}
            {campaign.scheduledAt && campaign.status === 'scheduled' && (
              <>
                {` · sends ${new Date(campaign.scheduledAt).toLocaleString()}`}
                {campaign.scheduleRelativeTo &&
                  ` (${relativeLabel(
                    campaign.scheduleRelativeTo,
                    campaign.scheduleOffsetMinutes ?? 0,
                  )})`}
              </>
            )}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={campaign.status}
          color={campaign.status === 'sent' ? 'success' : campaign.status === 'scheduled' ? 'warning' : 'default'}
          sx={{ textTransform: 'capitalize', fontWeight: 700 }}
        />
      </Stack>
      {campaign.status === 'draft' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          This campaign is a draft and hasn't been sent. Click{' '}
          <strong>Send / schedule</strong> when you're ready.
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {campaign.status === 'draft' && (
          <>
            <Button size="small" onClick={onEdit}>
              Edit
            </Button>
            <Button size="small" variant="contained" startIcon={<ScheduleSendRoundedIcon />} onClick={() => setScheduleOpen(true)}>
              Send / schedule
            </Button>
            <Button size="small" color="error" onClick={remove}>
              Delete
            </Button>
          </>
        )}
        {campaign.status === 'scheduled' && (
          <Button size="small" onClick={cancel}>
            Cancel (back to draft)
          </Button>
        )}
      </Stack>

      {scheduleOpen && (
        <ScheduleDialog
          eventId={eventId}
          campaignId={campaign.id}
          event={event}
          onClose={() => setScheduleOpen(false)}
          onDone={() => {
            setScheduleOpen(false)
            onChanged()
          }}
        />
      )}
    </Card>
  )
}

// Relative-offset presets expressed in minutes (negative = before the anchor).
const RELATIVE_UNITS: { value: string; label: string; minutes: number }[] = [
  { value: 'hours', label: 'hours', minutes: 60 },
  { value: 'days', label: 'days', minutes: 60 * 24 },
  { value: 'weeks', label: 'weeks', minutes: 60 * 24 * 7 },
]

/** Human label for a stored relative schedule, e.g. "1 week before event start". */
function relativeLabel(anchor: string, offsetMinutes: number): string {
  const anchorLabel = anchor === 'event_end' ? 'event end' : 'event start'
  if (offsetMinutes === 0) return `at ${anchorLabel}`
  const before = offsetMinutes < 0
  const abs = Math.abs(offsetMinutes)
  const unit =
    abs % (60 * 24 * 7) === 0
      ? { n: abs / (60 * 24 * 7), u: 'week' }
      : abs % (60 * 24) === 0
        ? { n: abs / (60 * 24), u: 'day' }
        : { n: abs / 60, u: 'hour' }
  const plural = unit.n === 1 ? unit.u : `${unit.u}s`
  return `${unit.n} ${plural} ${before ? 'before' : 'after'} ${anchorLabel}`
}

function ScheduleDialog({
  eventId,
  campaignId,
  event,
  onClose,
  onDone,
}: {
  eventId: string
  campaignId: string
  event: EventInfo | null
  onClose: () => void
  onDone: () => void
}) {
  const { notify } = useToast()
  const [mode, setMode] = useState<'now' | 'absolute' | 'relative'>('now')
  const [when, setWhen] = useState('')
  const [relAnchor, setRelAnchor] = useState<'event_start' | 'event_end'>('event_start')
  const [relBefore, setRelBefore] = useState(true)
  const [relAmount, setRelAmount] = useState(1)
  const [relUnit, setRelUnit] = useState('weeks')
  const [saving, setSaving] = useState(false)

  const offsetMinutes = () => {
    const unit = RELATIVE_UNITS.find((u) => u.value === relUnit)!
    const mag = Math.max(0, relAmount) * unit.minutes
    return relBefore ? -mag : mag
  }

  // Preview the resolved absolute send time for the relative mode.
  const relPreview = () => {
    if (!event) return null
    const anchorIso = relAnchor === 'event_end' ? event.endsAt : event.startsAt
    if (!anchorIso) return null
    return new Date(new Date(anchorIso).getTime() + offsetMinutes() * 60000)
  }

  const go = async () => {
    setSaving(true)
    try {
      if (mode === 'now') {
        await adminService.scheduleCampaign(eventId, campaignId, {})
        notify('Sending now')
      } else if (mode === 'absolute') {
        await adminService.scheduleCampaign(eventId, campaignId, {
          sendAt: new Date(when).toISOString(),
        })
        notify('Scheduled')
      } else {
        await adminService.scheduleCampaign(eventId, campaignId, {
          relativeTo: relAnchor,
          offsetMinutes: offsetMinutes(),
        })
        notify('Scheduled')
      }
      onDone()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  const canSubmit =
    mode === 'now' ||
    (mode === 'absolute' && !!when) ||
    (mode === 'relative' && !!event && relAmount > 0)

  const preview = mode === 'relative' ? relPreview() : null

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Send campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="When to send"
            fullWidth
            size="small"
            value={mode}
            onChange={(e) => setMode(e.target.value as typeof mode)}
          >
            <MenuItem value="now">Send now</MenuItem>
            <MenuItem value="absolute">At a specific date &amp; time</MenuItem>
            <MenuItem value="relative">Relative to the event</MenuItem>
          </TextField>

          {mode === 'absolute' && (
            <TextField
              type="datetime-local"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          )}

          {mode === 'relative' && (
            <>
              <Stack direction="row" spacing={1}>
                <TextField
                  type="number"
                  label="Amount"
                  size="small"
                  sx={{ width: 100 }}
                  value={relAmount}
                  onChange={(e) => setRelAmount(Number(e.target.value))}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  select
                  label="Unit"
                  size="small"
                  sx={{ flex: 1 }}
                  value={relUnit}
                  onChange={(e) => setRelUnit(e.target.value)}
                >
                  {RELATIVE_UNITS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  label="Before / after"
                  size="small"
                  sx={{ flex: 1 }}
                  value={relBefore ? 'before' : 'after'}
                  onChange={(e) => setRelBefore(e.target.value === 'before')}
                >
                  <MenuItem value="before">before</MenuItem>
                  <MenuItem value="after">after</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Event anchor"
                  size="small"
                  sx={{ flex: 1 }}
                  value={relAnchor}
                  onChange={(e) =>
                    setRelAnchor(e.target.value as 'event_start' | 'event_end')
                  }
                >
                  <MenuItem value="event_start">the event starts</MenuItem>
                  <MenuItem value="event_end">the event ends</MenuItem>
                </TextField>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {preview
                  ? `Sends ${preview.toLocaleString()}`
                  : 'The event has no start/end date set.'}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={go} disabled={saving || !canSubmit}>
          {mode === 'now' ? 'Send now' : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function SurveyDialog({
  eventId,
  forms,
  onClose,
  onSaved,
}: {
  eventId: string
  forms: EventForm[]
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [formId, setFormId] = useState(forms[0]?.id ?? '')
  const [trigger, setTrigger] = useState('event_end')
  const [offset, setOffset] = useState(60)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!formId) return
    setSaving(true)
    try {
      await adminService.createSurveySchedule(eventId, { formId, trigger, offsetMinutes: offset })
      notify('Survey scheduled')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Schedule a survey</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Form" fullWidth value={formId} onChange={(e) => setFormId(e.target.value)}>
            {forms.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Send" fullWidth value={trigger} onChange={(e) => setTrigger(e.target.value)}>
            <MenuItem value="event_end">After the event ends</MenuItem>
          </TextField>
          <TextField label="Minutes after" type="number" fullWidth value={offset} onChange={(e) => setOffset(Number(e.target.value))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function CampaignDialog({
  eventId,
  campaign,
  rules,
  onClose,
  onSaved,
  onSavedAndSchedule,
}: {
  eventId: string
  campaign: Campaign | null
  rules: TargetingRule[]
  onClose: () => void
  onSaved: () => void
  onSavedAndSchedule: (saved: Campaign) => void
}) {
  const { notify } = useToast()
  const [name, setName] = useState(campaign?.name ?? '')
  const [subject, setSubject] = useState(campaign?.subject ?? '')
  const [source, setSource] = useState(campaign?.bodySource || campaign?.bodyHtml || '')
  const [initialHtml] = useState(campaign?.bodySource || campaign?.bodyHtml || '')
  const [ruleId, setRuleId] = useState(campaign?.targetingRuleId ?? '')
  const [saving, setSaving] = useState(false)

  const persist = async (): Promise<Campaign | null> => {
    if (!name.trim() || !subject.trim()) {
      notify('Name and subject are required', 'error')
      return null
    }
    const payload = { name, subject, bodySource: source, targetingRuleId: ruleId || null }
    if (campaign) return adminService.updateCampaign(eventId, campaign.id, payload)
    return adminService.createCampaign(eventId, payload)
  }

  const save = async () => {
    setSaving(true)
    try {
      const saved = await persist()
      if (!saved) return
      notify(campaign ? 'Campaign updated' : 'Campaign created')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  const saveAndSchedule = async () => {
    setSaving(true)
    try {
      const saved = await persist()
      if (!saved) return
      onSavedAndSchedule(saved)
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{campaign ? 'Edit campaign' : 'New campaign'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name (internal)" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="Subject" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} />
          <TextField select label="Send to" fullWidth value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
            <MenuItem value="">Everyone</MenuItem>
            {rules.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Message
            </Typography>
            <RichTextEditor valueHtml={initialHtml} onChangeHtml={setSource} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
        <Button
          variant="contained"
          startIcon={<ScheduleSendRoundedIcon />}
          onClick={saveAndSchedule}
          disabled={saving}
        >
          Save &amp; send/schedule
        </Button>
      </DialogActions>
    </Dialog>
  )
}
